import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import loadRecaptcha from '../../utilities/load-recaptcha';

const propTypes = {
    id: PropTypes.string.isRequired,
    siteKey: PropTypes.string.isRequired,
    className: PropTypes.string,
    verifyCallback: PropTypes.func,
    expiredCallback: PropTypes.func,
    errorCallback: PropTypes.func,
    theme: PropTypes.oneOf(['light', 'dark']),
    size: PropTypes.oneOf(['normal', 'compact'])
};

function ReCaptcha(props) {
    const {
        id,
        className = '',
        siteKey,
        verifyCallback,
        expiredCallback,
        errorCallback,
        theme,
        size
    } = props;

    // The widget still has to invoke the current handlers, so they are held in a ref rather
    // than captured, and the widget is not recreated when their identity changes.
    const handlersRef = useRef({ verifyCallback, expiredCallback, errorCallback });

    useEffect(() => {
        handlersRef.current = { verifyCallback, expiredCallback, errorCallback };
    });

    useEffect(() => {
        let isCurrent = true;

        loadRecaptcha()
            .then(grecaptcha => {
                if (!isCurrent) {
                    return;
                }

                grecaptcha.render(id, {
                    'sitekey': siteKey,
                    'callback': token => handlersRef.current.verifyCallback?.(token),
                    'expired-callback': () => handlersRef.current.expiredCallback?.(),
                    'error-callback': () => handlersRef.current.errorCallback?.(),
                    theme,
                    size
                });
            })
            .catch(error => {
                // The widget stays absent, which the form's submit guard already blocks on.
                // Logging is what makes that visible instead of looking like a dead page.
                console.error('reCAPTCHA failed to load.', error);
            });

        return () => {
            isCurrent = false;
        };
        // Runs once per mount by design: reCAPTCHA throws if an element is rendered into
        // twice, so id, siteKey, theme and size are fixed when the widget is created.
        // To change any of them, remount the component with a different `key`.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="asc-form-group ul-recaptcha-container">
            <div id={id} className={`ul-recaptcha ${className}`} />
        </div>
    );
}

ReCaptcha.propTypes = propTypes;
export default ReCaptcha;
